import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';

interface Vehicle { id: string; name: string; description: string | null; capacity: number; min_passengers: number; image_url: string | null; badge: string | null; features: string[]; service_type_id: string | null; sort_order: number; is_active: boolean; }
interface ServiceType { id: string; name: string; }

const emptyForm = { name: '', description: '', capacity: 4, min_passengers: 1, image_url: '', badge: '', features: '', service_type_id: '', is_active: true, sort_order: 0 };

const AdminShuttleVehicles = () => {
  const { toast } = useToast();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [v, st] = await Promise.all([
      supabase.from('transfer_vehicles').select('*').order('sort_order'),
      supabase.from('service_types').select('*').order('sort_order'),
    ]);
    setVehicles((v.data || []) as Vehicle[]);
    setServiceTypes((st.data || []) as ServiceType[]);
    setLoading(false);
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (v: Vehicle) => {
    setEditing(v);
    setForm({ name: v.name, description: v.description || '', capacity: v.capacity, min_passengers: v.min_passengers, image_url: v.image_url || '', badge: v.badge || '', features: (v.features || []).join(', '), service_type_id: v.service_type_id || '', is_active: v.is_active, sort_order: v.sort_order });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload: any = {
      name: form.name, description: form.description || null, capacity: form.capacity, min_passengers: form.min_passengers,
      image_url: form.image_url || null, badge: form.badge || null,
      features: form.features ? form.features.split(',').map(f => f.trim()).filter(Boolean) : [],
      service_type_id: form.service_type_id || null, is_active: form.is_active, sort_order: form.sort_order,
    };
    try {
      if (editing) {
        const { error } = await supabase.from('transfer_vehicles').update(payload).eq('id', editing.id) as any;
        if (error) throw error;
      } else {
        const { error } = await supabase.from('transfer_vehicles').insert(payload) as any;
        if (error) throw error;
      }
      toast({ title: editing ? 'Updated' : 'Created' });
      setDialogOpen(false);
      fetchAll();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('transfer_vehicles').delete().eq('id', id) as any;
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); fetchAll(); }
  };

  const getSTName = (id: string | null) => serviceTypes.find(s => s.id === id)?.name || '-';

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Transfer Vehicles</h1>
            <p className="text-muted-foreground">Manage vehicles for the shuttle system</p>
          </div>
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Add Vehicle</Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Capacity</TableHead>
                  <TableHead>Service Type</TableHead>
                  <TableHead>Badge</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : vehicles.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8">No vehicles found.</TableCell></TableRow>
                ) : vehicles.map(v => (
                  <TableRow key={v.id}>
                    <TableCell>{v.image_url ? <img src={v.image_url} alt={v.name} className="w-16 h-10 object-cover rounded" /> : '-'}</TableCell>
                    <TableCell className="font-medium">{v.name}</TableCell>
                    <TableCell>{v.min_passengers}-{v.capacity} pax</TableCell>
                    <TableCell>{getSTName(v.service_type_id)}</TableCell>
                    <TableCell>{v.badge || '-'}</TableCell>
                    <TableCell>{v.is_active ? '✅' : '❌'}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(v)}><Pencil className="w-4 h-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {v.name}?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(v.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle></DialogHeader>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Capacity (max pax)</Label><Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: parseInt(e.target.value) || 1})} /></div>
                <div><Label>Min Passengers</Label><Input type="number" value={form.min_passengers} onChange={e => setForm({...form, min_passengers: parseInt(e.target.value) || 1})} /></div>
              </div>
              <div><Label>Image URL</Label><Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Badge</Label><Input value={form.badge} onChange={e => setForm({...form, badge: e.target.value})} placeholder="e.g. Most Popular" /></div>
                <div>
                  <Label>Service Type</Label>
                  <Select value={form.service_type_id} onValueChange={v => setForm({...form, service_type_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{serviceTypes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Features (comma-separated)</Label><Input value={form.features} onChange={e => setForm({...form, features: e.target.value})} placeholder="WiFi, AC, Water" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})} /></div>
                <div className="flex items-center gap-2 pt-6"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label>Active</Label></div>
              </div>
              <Button onClick={handleSave} className="w-full">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminShuttleVehicles;
